"use client"

import { useEffect, useState } from 'react';
import BusinessService from '../../api/businesses.service';
import { ArrowLeft, Loader2, Edit2, Building2, Mail, Phone, Globe, MapPin, FileText, CreditCard, Users, BarChart4, ReceiptText, Landmark, ExternalLink, Plus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import FormModal from '@/components/FormModal';
import { EditBusinessForm } from '@/pages/businesses/EditBusinessForm';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BusinessTaxManagement } from '@/pages/taxes/BusinessTaxInfo';
import BankAccountsService, { BankAccount } from '@/api/bankAccounts.service';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Check } from 'lucide-react';

interface BusinessDetailsProps {
  publicId: string;
}

export default function Business({ publicId }: BusinessDetailsProps) {
  const [isModalOpen, setModalOpen] = useState(false);
  const [business, setBusiness] = useState<any>(null);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingBankAccounts, setIsLoadingBankAccounts] = useState(false);
  const { toast } = useToast();
  
  useEffect(() => {
    const fetchBusiness = async () => {
      setIsLoading(true);
      try {
        const response = await BusinessService.getBusinessByPublicId(publicId);
        if (response.data.result === 'invalid') {
          window.location.href = '/logout';
        } else if (response.data.result === 'success') {
          setBusiness(response.data.business);
          
          // After fetching business details, fetch bank accounts
          if (response.data.business.publicId) {
            fetchBankAccounts(response.data.business.publicId);
          }
        } else {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load business details",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    if (publicId) {
      fetchBusiness();
    }
  }, [publicId, toast]);

  const fetchBankAccounts = async (businessPublicId: string) => {
    setIsLoadingBankAccounts(true);
    try {
      const response = await BankAccountsService.getBusinessBankAccounts(businessPublicId);
      if (response.data.result === 'success') {
        setBankAccounts(response.data.businessBankAccounts || []);
      }
    } catch (error) {
      console.error("Failed to fetch bank accounts", error);
    } finally {
      setIsLoadingBankAccounts(false);
    }
  };

  // Mask account number for display
  const maskAccountNumber = (accountNumber: string) => {
    if (accountNumber.length <= 4) return accountNumber;
    return '••••' + accountNumber.slice(-4);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-slate-50 dark:bg-slate-900">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading business details...</p>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-slate-50 dark:bg-slate-900">
        <p className="text-muted-foreground">Business not found</p>
        <Button onClick={() => window.history.back()} variant="outline" className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
        </Button>
      </div>
    );
  }

  const formatBusinessType = (type: string) => {
    const types: Record<string, string> = {
      "limited_liability_company": "Private Limited Company",
      "partnership": "Partnership",
      "corporation": "Corporation",
      "nonprofit": "Non-Profit Organization",
      "ngo": "Non-Governmental Organization",
      "trust": "Trust"
    };
    
    return types[type] || type;
  };

  return (
    <div className="container py-8 mx-auto max-w-7xl">
      <div className="flex flex-col space-y-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => window.history.back()} 
              variant="ghost" 
              size="sm"
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-2xl font-bold tracking-tight">{business.name}</h1>
            <Badge variant="outline" className="ml-2 bg-primary/10 text-primary border-primary/20">
              {formatBusinessType(business.businessType)}
            </Badge>
          </div>
          
          <Button 
            onClick={() => setModalOpen(true)} 
            variant="outline"
            className="gap-2 border-primary/20 text-primary hover:bg-primary/10"
          >
            <Edit2 className="w-4 h-4" />
            Edit Business
          </Button>
        </div>
        
        {/* Banner Image */}
        {business.bannerImg && (
          <div className="w-full h-48 md:h-64 overflow-hidden rounded-xl shadow-sm">
            <img 
              src={business.bannerImg} 
              alt="Business Banner" 
              className="w-full h-full object-cover" 
            />
          </div>
        )}
        
        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Business Profile */}
          <Card className="md:col-span-1 shadow-sm border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Business Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo */}
              {business.logoImg && (
                <div className="flex justify-center mb-6">
                  <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-md">
                    <img 
                      src={business.logoImg} 
                      alt={business.name} 
                      className="w-full h-full object-cover" 
                    />
                  </div>
                </div>
              )}
              
              {/* Contact Info */}
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Email</p>
                    <p className="text-sm text-muted-foreground">{business.emailAddress}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Phone</p>
                    <p className="text-sm text-muted-foreground">{business.phoneNumber}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Address</p>
                    <p className="text-sm text-muted-foreground">{business.address}</p>
                    <p className="text-sm text-muted-foreground">{business.city}, {business.country}</p>
                  </div>
                </div>
                
                {business.website && (
                  <div className="flex items-start gap-3">
                    <Globe className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Website</p>
                      <a 
                        href={business.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        {business.website}
                      </a>
                    </div>
                  </div>
                )}
              </div>
              
              <Separator />
              
              {/* Quick Actions */}
              <div className="space-y-2">
                <p className="text-sm font-medium">Quick Actions</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <FileText className="w-4 h-4 mr-2" />
                    Invoices
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Payments
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full justify-start"
                    onClick={() => window.location.href = `/business/bank-accounts/${business.publicId}`}
                  >
                    <Landmark className="w-4 h-4 mr-2" />
                    Bank Accounts
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <Users className="w-4 h-4 mr-2" />
                    Customers
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <BarChart4 className="w-4 h-4 mr-2" />
                    Reports
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <ReceiptText className="w-4 h-4 mr-2" />
                    Tax Information
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Right Column - Business Details */}
          <div className="md:col-span-2 space-y-6">
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid grid-cols-4 w-full md:w-[500px]">
                <TabsTrigger value="details">Business Details</TabsTrigger>
                <TabsTrigger value="description">Description</TabsTrigger>
                <TabsTrigger value="bank-accounts">Bank Accounts</TabsTrigger>
                <TabsTrigger value="tax">Tax Info</TabsTrigger>
              </TabsList>
              
              <TabsContent value="details" className="mt-4">
                <Card className="shadow-sm border-slate-200 dark:border-slate-800">
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Business Name</p>
                        <p className="font-medium">{business.name}</p>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Business Type</p>
                        <p className="font-medium">{formatBusinessType(business.businessType)}</p>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Email Address</p>
                        <p className="font-medium">{business.emailAddress}</p>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Phone Number</p>
                        <p className="font-medium">{business.phoneNumber}</p>
                      </div>
                      
                      <div className="space-y-1 md:col-span-2">
                        <p className="text-sm text-muted-foreground">Address</p>
                        <p className="font-medium">{business.address}, {business.city}, {business.country}</p>
                      </div>
                      
                      {business.website && (
                        <div className="space-y-1 md:col-span-2">
                          <p className="text-sm text-muted-foreground">Website</p>
                          <a 
                            href={business.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="font-medium text-primary hover:underline"
                          >
                            {business.website}
                          </a>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="description" className="mt-4">
                <Card className="shadow-sm border-slate-200 dark:border-slate-800">
                  <CardContent className="pt-6">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Business Description</p>
                      <div className="mt-2 text-sm leading-relaxed">
                        {business.description ? (
                          <p>{business.description}</p>
                        ) : (
                          <p className="text-muted-foreground italic">No description provided</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="bank-accounts" className="mt-4">
                <Card className="shadow-sm border-slate-200 dark:border-slate-800">
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Landmark className="w-5 h-5 text-primary" />
                        Bank Accounts
                      </CardTitle>
                      <CardDescription>
                        Manage bank accounts to receive payments
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => window.location.href = `/business/bank-accounts/${business.publicId}`}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View All
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoadingBankAccounts ? (
                      <div className="flex justify-center items-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        <span className="ml-2 text-muted-foreground">Loading bank accounts...</span>
                      </div>
                    ) : bankAccounts.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Account Name</TableHead>
                            <TableHead>Bank</TableHead>
                            <TableHead>Account Number</TableHead>
                            <TableHead>Type</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {bankAccounts.slice(0, 3).map((account) => (
                            <TableRow key={account.id}>
                              <TableCell className="font-medium">
                                <div className="flex items-center">
                                  {account.accountName}
                                  {account.isPrimary && (
                                    <Badge variant="outline" className="ml-2 bg-green-50 text-green-700 border-green-200 text-xs">
                                      <Check className="h-3 w-3 mr-1" />
                                      Primary
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>{account.bankName}</TableCell>
                              <TableCell>{maskAccountNumber(account.accountNumber)}</TableCell>
                              <TableCell>
                                <Badge variant="secondary" className="capitalize">
                                  {account.accountType}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="text-center py-8 border border-dashed rounded-md">
                        <Landmark className="h-10 w-10 text-muted-foreground/50 mx-auto mb-3" />
                        <h3 className="text-base font-medium text-muted-foreground mb-2">No Bank Accounts</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Add bank accounts to receive payments from your customers.
                        </p>
                        <Button 
                          size="sm" 
                          onClick={() => window.location.href = `/businesses/bank-accounts/${business.publicId}`}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Bank Account
                        </Button>
                      </div>
                    )}
                    
                    {bankAccounts.length > 3 && (
                      <div className="mt-4 text-center">
                        <Button 
                          variant="link" 
                          onClick={() => window.location.href = `/business/bank-accounts/${business.publicId}`}
                        >
                          View all {bankAccounts.length} bank accounts
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="tax" className="mt-4">
                <Card className="shadow-sm border-slate-200 dark:border-slate-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ReceiptText className="w-5 h-5 text-primary" />
                      Tax Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <BusinessTaxManagement businessId={business.id} />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
            
            {/* Financial Overview Card */}
            <Card className="shadow-sm border-slate-200 dark:border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart4 className="w-5 h-5 text-primary" />
                  Financial Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-primary/5 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Outstanding Invoices</p>
                    <p className="text-2xl font-bold mt-1">$0.00</p>
                    <p className="text-xs text-muted-foreground mt-2">0 invoices pending</p>
                  </div>
                  
                  <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Paid This Month</p>
                    <p className="text-2xl font-bold mt-1 text-green-600 dark:text-green-400">$0.00</p>
                    <p className="text-xs text-muted-foreground mt-2">0 payments received</p>
                  </div>
                  
                  <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Total Customers</p>
                    <p className="text-2xl font-bold mt-1 text-blue-600 dark:text-blue-400">0</p>
                    <p className="text-xs text-muted-foreground mt-2">0 active this month</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      <FormModal
        open={isModalOpen}
        setOpen={setModalOpen}
        title="Update Business"
      >
        <EditBusinessForm business={business} />
      </FormModal>
    </div>
  );
}
